import React from "react";
import { ExpandedCardStates } from "./newEntryTypes";

/**
 * Blur / click-outside behavior while one parent section is expanded.
 * Inactive sections dim and swallow clicks so the user dismisses (or is prompted to save) instead of expanding another card.
 */

/** Class names for active vs inactive parent section padding/blur/opacity/z-index. */
export const getParentSectionFocusClassName = (expandedCard: ExpandedCardStates, card: ExpandedCardStates): string => {
    if (!expandedCard) return "parent-section-focus";
    const isActiveSection = card === expandedCard;
    return `parent-section-focus ${isActiveSection ? "parent-section-focus--active" : "parent-section-focus--dimmed"}`;
};

/** Collapse immediately, or open the unsaved-changes modal when the expanded card is dirty. */
export const collapseFromBlurredArea = (
    expandedCard: ExpandedCardStates,
    hasUnsavedChangesInExpandedCard: boolean,
    collapseExpandedCard: () => void,
    promptUnsavedChanges: () => void
) => {
    if (!expandedCard) return;
    // Event Info and Summary have no dirty form to protect; other sections prompt if unsaved.
    if (expandedCard === "header" || expandedCard === "summary" || !hasUnsavedChangesInExpandedCard) {
        collapseExpandedCard();
        return;
    }
    promptUnsavedChanges();
};

/** Click on a blurred sibling section: collapse (or prompt) instead of expanding that sibling. */
export const handleBlurredSectionClick = (expandedCard: ExpandedCardStates, card: ExpandedCardStates, onDismiss: () => void) => (
    event: React.MouseEvent<HTMLElement>
) => {
    if (!expandedCard || card === expandedCard) return;

    event.preventDefault();
    event.stopPropagation();
    onDismiss();
};

/** Full-size hit target over a blurred section so clicks dismiss instead of reaching the card underneath. */
export const ParentSectionBlurOverlay: React.FC<{
    expandedCard: ExpandedCardStates;
    card: ExpandedCardStates;
    onDismiss: () => void;
}> = ({ expandedCard, card, onDismiss }) => {
    if (!expandedCard || card === expandedCard) return null;
    return (
        <div
            aria-hidden
            className="parent-section-blur-overlay"
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onDismiss();
            }}
        />
    );
};

/** Page-level overlay behind all sections; clicking empty space collapses the open card. */
export const PageSectionBlurBackdrop: React.FC<{
    isActive: boolean;
    onDismiss: () => void;
}> = ({ isActive, onDismiss }) => {
    if (!isActive) return null;
    return (
        <div
            aria-hidden
            className="parent-section-blur-backdrop"
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onDismiss();
            }}
        />
    );
};

interface ParentSectionFocusWrapProps {
    id?: string;
    card: ExpandedCardStates;
    expandedCard: ExpandedCardStates;
    onDismiss: () => void;
    children: React.ReactNode;
    className?: string;
}

/** Wrapper for Event Info / Stabling / Waivers / Summary: scroll id, blur style, and click-to-dismiss overlay. */
export const ParentSectionFocusWrap: React.FC<ParentSectionFocusWrapProps> = ({ id, card, expandedCard, onDismiss, children, className }) => {
    const focusClassName = getParentSectionFocusClassName(expandedCard, card);
    return (
        <div
            id={id}
            className={className ? `${focusClassName} ${className}` : focusClassName}
            onClickCapture={handleBlurredSectionClick(expandedCard, card, onDismiss)}>
            <ParentSectionBlurOverlay expandedCard={expandedCard} card={card} onDismiss={onDismiss} />
            {children}
        </div>
    );
};
